import React, { createContext, useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { db } from '../firebase';
import { useAuth } from './AuthContext';

const BranchContext = createContext();

export function useBranch() {
  return useContext(BranchContext);
}

const STORAGE_KEY = 'activeBranchId';

/**
 * Provider global de Sucursales.
 * - Carga todas las sucursales activas en tiempo real.
 * - Determina las sucursales accesibles según el usuario (admin ve todas).
 * - Mantiene la sucursal activa (persistida en localStorage), con opción "Todas".
 * - Expone helper filterByBranch para filtrar cualquier colección por la sucursal activa.
 */
export function BranchProvider({ children }) {
  const { userData } = useAuth();
  const [branches, setBranches] = useState([]);
  const [loadingBranches, setLoadingBranches] = useState(true);
  const [activeBranchId, setActiveBranchIdState] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || 'all'; } catch { return 'all'; }
  });

  // Cargar sucursales activas en tiempo real
  useEffect(() => {
    const unsub = onSnapshot(
      collection(db, 'branches'),
      (snap) => {
        const list = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((b) => b.active !== false)
          .sort((a, b) => (a.name || '').localeCompare(b.name || ''));
        setBranches(list);
        setLoadingBranches(false);
      },
      () => { setBranches([]); setLoadingBranches(false); }
    );
    return unsub;
  }, []);

  const isAdmin = userData?.role === 'admin';
  const isDirectorMedico = userData?.role === 'Director Médico';

  // Sucursales accesibles:
  // - admin ve todas;
  // - Director Médico solo su primera sucursal asignada;
  // - el resto según su branchIds asignado.
  const accessibleBranches = useMemo(() => {
    if (isAdmin) return branches;
    let ids = userData?.branchIds || [];
    if (isDirectorMedico && ids.length > 1) ids = [ids[0]];
    if (!ids.length) return branches; // Sin asignación explícita → ve todas (compatibilidad)
    const ordered = branches.filter((b) => ids.includes(b.id));
    ordered.sort((a, b) => ids.indexOf(a.id) - ids.indexOf(b.id));
    return ordered;
  }, [branches, userData, isAdmin, isDirectorMedico]);

  const accessibleBranchIds = useMemo(
    () => accessibleBranches.map((b) => b.id),
    [accessibleBranches]
  );

  // Validar que la sucursal activa sea válida y accesible
  useEffect(() => {
    if (loadingBranches) return;
    if (!accessibleBranchIds.length) {
      if (activeBranchId !== 'all') {
        setActiveBranchIdState('all');
        try { localStorage.setItem(STORAGE_KEY, 'all'); } catch {}
      }
      return;
    }
    if (isAdmin) {
      if (activeBranchId === 'all') return;
      if (!accessibleBranchIds.includes(activeBranchId)) {
        setActiveBranchIdState('all');
        try { localStorage.setItem(STORAGE_KEY, 'all'); } catch {}
      }
    } else {
      // Usuarios no administradores deben operar con una sucursal específica (no "todas")
      if (activeBranchId === 'all' || !accessibleBranchIds.includes(activeBranchId)) {
        const next = accessibleBranchIds[0];
        setActiveBranchIdState(next);
        try { localStorage.setItem(STORAGE_KEY, next); } catch {}
      }
    }
  }, [loadingBranches, activeBranchId, accessibleBranchIds, isAdmin]);

  const setActiveBranchId = useCallback((id) => {
    setActiveBranchIdState(id);
    try { localStorage.setItem(STORAGE_KEY, id); } catch {}
  }, []);

  const isAllBranches = activeBranchId === 'all';

  // IDs de sucursal objetivo para el filtrado actual
  const targetBranchIds = useMemo(
    () => (isAllBranches ? accessibleBranchIds : [activeBranchId]),
    [isAllBranches, accessibleBranchIds, activeBranchId]
  );

  /**
   * Filtra una lista de documentos por la sucursal activa.
   * Soporta campo array (branchIds) o singular (branchId).
   * Los documentos sin sucursal asignada (datos legados) se muestran siempre.
   * @param {Array} items
   * @param {object} opts { field, includeUnassigned }
   */
  const filterByBranch = useCallback((items, opts = {}) => {
    const { includeUnassigned = true } = opts;
    if (!Array.isArray(items)) return items;
    return items.filter((it) => {
      const itemIds = Array.isArray(it?.branchIds)
        ? it.branchIds
        : (it?.branchId ? [it.branchId] : []);
      if (itemIds.length === 0) return includeUnassigned;
      return itemIds.some((id) => targetBranchIds.includes(id));
    });
  }, [targetBranchIds]);

  const branchName = useCallback(
    (id) => branches.find((b) => b.id === id)?.name || '',
    [branches]
  );

  const activeBranch = useMemo(
    () => branches.find((b) => b.id === activeBranchId) || (accessibleBranches[0] || {}),
    [branches, activeBranchId, accessibleBranches]
  );

  const value = {
    branches,
    loadingBranches,
    accessibleBranches,
    accessibleBranchIds,
    activeBranchId,
    activeBranch,
    setActiveBranchId,
    isAllBranches,
    targetBranchIds,
    filterByBranch,
    branchName,
    isAdmin,
  };

  return (
    <BranchContext.Provider value={value}>
      {children}
    </BranchContext.Provider>
  );
}
